import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Compass, ArrowLeft, ChevronRight, Loader2, Plus, Check,
  Flower2, Dumbbell, Wallet, Brain, Sparkles
} from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import AddYourOwnRitual from '../components/AddYourOwnRitual';

const CATEGORIES = [
  {
    id: 'meditations',
    icon: Flower2,
    en: { title: 'My Meditations', desc: 'Breathwork, mindfulness, and inner stillness' },
    tr: { title: 'Meditasyonlarım', desc: 'Nefes çalışmaları, farkındalık ve iç sessizlik' },
    color: 'from-violet-500/20 to-purple-500/20',
    borderColor: 'border-violet-500/30',
    iconColor: 'text-violet-400',
    diffColors: { easy: 'text-emerald-400', medium: 'text-amber-400', deep: 'text-rose-400' },
  },
  {
    id: 'physical',
    icon: Dumbbell,
    en: { title: 'Physical Practices', desc: 'Movement, strength, and body awareness' },
    tr: { title: 'Fiziksel Pratikler', desc: 'Hareket, güç ve bedensel farkındalık' },
    color: 'from-emerald-500/20 to-green-500/20',
    borderColor: 'border-emerald-500/30',
    iconColor: 'text-emerald-400',
    diffColors: { easy: 'text-emerald-400', medium: 'text-amber-400', deep: 'text-rose-400' },
  },
  {
    id: 'financial',
    icon: Wallet,
    en: { title: 'Financial Practices', desc: 'Money awareness and financial health' },
    tr: { title: 'Finansal Pratikler', desc: 'Para farkındalığı ve finansal sağlık' },
    color: 'from-amber-500/20 to-yellow-500/20',
    borderColor: 'border-amber-500/30',
    iconColor: 'text-amber-400',
    diffColors: { easy: 'text-emerald-400', medium: 'text-amber-400', deep: 'text-rose-400' },
  },
  {
    id: 'psyche',
    icon: Brain,
    en: { title: 'Emotions & Psyche', desc: 'Shadow work, journaling, and emotional depth' },
    tr: { title: 'Duygular & Ruh', desc: 'Gölge çalışması, günlük tutma ve duygusal derinlik' },
    color: 'from-rose-500/20 to-pink-500/20',
    borderColor: 'border-rose-500/30',
    iconColor: 'text-rose-400',
    diffColors: { easy: 'text-emerald-400', medium: 'text-amber-400', deep: 'text-rose-400' },
  },
];

const INtegrate = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [expandedCard, setExpandedCard] = useState(null);
  const [practiceData, setPracticeData] = useState({});
  const [loadingCategory, setLoadingCategory] = useState(null);
  const [addedMissions, setAddedMissions] = useState(new Set());

  const handleExpand = async (categoryId) => {
    if (expandedCard === categoryId) {
      setExpandedCard(null);
      return;
    }
    setExpandedCard(categoryId);

    // Fetch if not already loaded
    if (!practiceData[categoryId]) {
      setLoadingCategory(categoryId);
      try {
        const response = await api.get(`/practices/${categoryId}`);
        setPracticeData(prev => ({ ...prev, [categoryId]: response.data.practices }));
      } catch (error) {
        console.error('Failed to fetch practices:', error);
        toast.error(language === 'tr' ? 'Pratikler yüklenemedi' : 'Failed to load practices');
      } finally {
        setLoadingCategory(null);
      }
    }
  };

  const addToMissions = async (title) => {
    try {
      await api.post('/missions', {
        text: title,
        domain: 'navigator',
        frequency: 'daily',
        source: 'practice'
      });
      setAddedMissions(prev => new Set([...prev, title]));
      toast.success(language === 'tr' ? 'Misyona eklendi' : 'Added to missions');
    } catch {
      toast.error(language === 'tr' ? 'Eklenemedi' : 'Failed to add');
    }
  };

  return (
    <div className="min-h-screen relative py-12 px-6">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-white/50 hover:text-white transition-colors flex items-center gap-2 text-sm font-sans uppercase tracking-widest"
            data-testid="rituals-back"
          >
            <ArrowLeft className="w-4 h-4" />
            {language === 'tr' ? 'Panel' : 'Dashboard'}
          </button>
        </div>

        <div className="text-center mb-8">
          <Compass className="w-8 h-8 text-[#D4AF37] mx-auto mb-2" />
          <h1 className="font-serif text-3xl text-[#F8F5F2]">
            {language === 'tr' ? 'Ritüeller & Pratikler' : 'Rituals & Practices'}
          </h1>
          <p className="text-[#6B7280] font-sans text-sm mt-2">
            {language === 'tr' ? 'Haritanıza göre kişiselleştirilmiş' : 'Personalized to your Sun Map'}
          </p>
        </div>

        {/* Practice Cards */}
        <div className="space-y-4">
          {CATEGORIES.map((cat, idx) => {
            const Icon = cat.icon;
            const isExpanded = expandedCard === cat.id;
            const isLoading = loadingCategory === cat.id;
            const practices = practiceData[cat.id] || [];
            const content = cat[language] || cat.en;

            return (
              <motion.div
                key={cat.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className={`rounded-2xl border ${cat.borderColor} bg-gradient-to-br ${cat.color} overflow-hidden`}
                data-testid={`practice-card-${cat.id}`}
              >
                <button
                  onClick={() => handleExpand(cat.id)}
                  className="w-full p-5 flex items-center justify-between text-left"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-black/20 flex items-center justify-center">
                      <Icon className={`w-5 h-5 ${cat.iconColor}`} />
                    </div>
                    <div>
                      <h3 className="font-serif text-lg text-[#F8F5F2]">{content.title}</h3>
                      <p className="text-[#A1A1AA] font-sans text-xs mt-0.5">{content.desc}</p>
                    </div>
                  </div>
                  <ChevronRight className={`w-5 h-5 text-[#6B7280] transition-transform duration-300 flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`} />
                </button>

                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-5 pb-5 space-y-2">
                        {isLoading ? (
                          <div className="flex items-center justify-center py-8 gap-2">
                            <Loader2 className="w-5 h-5 text-[#D4AF37] animate-spin" />
                            <span className="text-[#6B7280] font-sans text-sm">
                              {language === 'tr' ? 'Kişiselleştiriliyor...' : 'Personalizing for you...'}
                            </span>
                          </div>
                        ) : practices.length > 0 ? (
                          practices.map((practice, i) => {
                            const isAdded = addedMissions.has(practice.title);
                            return (
                              <div key={i} className="p-3 rounded-xl bg-black/20 border border-white/5">
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="text-[#F8F5F2] font-sans text-sm font-medium">{practice.title}</span>
                                      {practice.difficulty && (
                                        <span className={`text-[10px] uppercase tracking-wider ${cat.diffColors[practice.difficulty] || 'text-[#6B7280]'}`}>
                                          {practice.difficulty}
                                        </span>
                                      )}
                                    </div>
                                    {practice.why && (
                                      <p className="text-[#A1A1AA] font-sans text-xs leading-relaxed italic">{practice.why}</p>
                                    )}
                                  </div>
                                  <div className="flex flex-col gap-1.5 shrink-0">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const deepMsg = language === 'tr'
                                          ? `"${practice.title}" pratiğini benim için derinlemesine aç. Neden benim durumum için önemli, adım adım nasıl yapılır ve kaçınmam gereken tuzaklar neler? Kısa ve net.`
                                          : `Explain the practice "${practice.title}" in depth for me specifically. Why does it matter for my situation, how do I do it step-by-step, and what traps should I avoid? Be concise and grounded.`;
                                        navigate('/coaching', { state: { initialMessage: deepMsg } });
                                      }}
                                      className="p-2 rounded-lg bg-white/5 text-[#6B7280] hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-all"
                                      title={language === 'tr' ? 'Derinlemesine açıkla' : 'Explain in depth'}
                                      data-testid={`explain-practice-${i}`}
                                    >
                                      <Sparkles className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); if (!isAdded) addToMissions(practice.title); }}
                                      disabled={isAdded}
                                      className={`p-2 rounded-lg transition-all ${
                                        isAdded
                                          ? 'bg-emerald-500/20 text-emerald-400'
                                          : 'bg-white/5 text-[#6B7280] hover:text-[#D4AF37] hover:bg-[#D4AF37]/10'
                                      }`}
                                      title={language === 'tr' ? 'Misyona ekle' : 'Add to missions'}
                                      data-testid={`add-practice-${i}`}
                                    >
                                      {isAdded ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <p className="text-[#6B7280] font-sans text-sm text-center py-4">
                            {language === 'tr' ? 'Pratik bulunamadı' : 'No practices found'}
                          </p>
                        )}

                        {/* Add your own ritual — inline form */}
                        <AddYourOwnRitual
                          category={cat.id}
                          language={language}
                          onAdded={() => {
                            setAddedMissions(prev => new Set([...prev, `__custom_${cat.id}_${Date.now()}`]));
                            toast.success(language === 'tr' ? 'Kendi pratiğin eklendi' : 'Your practice added');
                          }}
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default INtegrate;
